import React, { useState } from "react";
import { Button } from "react-bootstrap";
import {
  CalendarDays,
  Video,
  HelpCircle,
  ExternalLink,
  Mail,
  Phone,
  X,
} from "lucide-react";

const FloatingHelpButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  let supportUrl = "https://support.nationalhealthclaim.com/hc/en-us/articles";

  const SUPPORT_ARTICLES = [
    {
      title: "Add an employee",
      slug: "227119967-Adding-an-employee",
      videoPlayer:
        "https://player.vimeo.com/video/876958629?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479",
      steps: [
        '1. Log in and click on the "Employees" tab.',
        '2. Click on the blue "+ Add employee" button.',
        "3. A pop-up window will appear. Enter in the employee’s information.",
        '4. Once you hit "Save," a second pop-up window will appear.',
        '5. After clicking the blue "Finish Employee Add" button, the system automatically brings up another pop-up window in case you need to add another employee.',
      ],
    },
    {
      title: "Remove an employee",
      slug: "227254228-Remove-an-employee",
      videoPlayer:
        "https://player.vimeo.com/video/876965678?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479",
      steps: [
        '1. Login and click on the "Employees" tab.',
        "2. Locate the employee whose coverage you want to terminate and click on their name.",
        '3. Click on the "Terminations" tab.',
        '4. Click on the blue "Terminate" link to trigger a pop-up window. ',
        '5. Enter the following details and click "Save" to complete the termination:',
        "",
        "   • Termination Date: When the employee resigned or was let go. This is for records only.",
        "   • Benefit End Date: The last day they have coverage. Only expenses before this date are covered.",
        "   • Access After Termination: Days the employee can still access their account.",
        "   • Termination Note: Add any relevant notes.",
      ],
    },
    {
      title: "Temporarily suspend employee coverage",
      slug: "227256648-Temporarily-suspend-employee-coverage",
      videoPlayer:
        "https://player.vimeo.com/video/876963729?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479",
      steps: [
        '1. Login and click on the "Employees" tab.',
        "2. Locate the employee whose coverage you want to suspend and click on their name.",
        '3. Click on the "Date Ranges" tab.',
        '4. Click the "+ Add Date Range" blue button to trigger a pop-up window.',
        "5. Within this pop-up window, you have the ability to complete a series of tasks including adding an inactive date range for an employee or their dependents, and applying Student Status to dependents.",
        '6. Click on "Save" to confirm your changes. ',
      ],
    },
    {
      title: "Add Dependents",
      slug: "227120067-Add-dependents",
      videoPlayer:
        "https://player.vimeo.com/video/876960558?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479",
      steps: [
        '1. Login and click on the "Employees" tab.',
        "2. Locate the employee whose dependents you want to add and click on their name.",
        '3. Click on the "Dependents" tab.',
        '4. Click the "+ Add Dependent" blue button to trigger a pop-up window.',
        "5. Enter in the dependent information.",
        "   Required fields for entry are: First name, last name, relation, date of birth.",
        "   Under the ‘Relation’ field the options are:",
        "   • Spouse: No age restrictions for coverage. ",
        "   • Child: Only eligible up to a maximum age determined by the plan setup. ",
        "   • Unconditional dependents: non-Spouse dependents with no age restrictions. Follow CRA guidelines to determine if a dependent qualifies as Unconditional.",
        "   Optional fields for entry are: Nickname, maiden name.",
        '6. Click on "Save" to confirm your changes. Repeat this process for multiple dependents.',
      ],
    },
    {
      title: "Remove dependents",
      slug: "228923668-Remove-dependents",
      videoPlayer:
        "https://player.vimeo.com/video/876960558?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479",
      steps: [
        '1. Login and click on the "Employees" tab.',
        "2. Locate the employee whose dependent you want to hide.",
        '3. Click on the "Dependents" tab.',
        "4. Locate the dependent. Click the “Edit” link.",
        "5. A pop-up will appear. Select the “Is Hidden” checkbox and click “Save.” ",
      ],
    },
    {
      title: "Add Student Status coverage to dependents",
      slug: "227254268-Add-Student-Status-coverage-to-dependents",
      videoPlayer:
        "https://player.vimeo.com/video/876963729?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479",
      steps: [
        '1. Login and click on the "Employees" tab.',
        "2. Locate the employee whose dependent you want to add Student Status to and click on their name.",
        '3. Click on the "Date Ranges" tab.',
        '4. Click the "+ Add Date Range" blue button to trigger a pop-up window.',
        '5. Click on "Save" to confirm your changes.',
      ],
    },
    {
      title: "How to submit funds",
      slug: "227128147-How-to-submit-funds",
      videoPlayer:
        "https://player.vimeo.com/video/869672197?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479",
      steps: [
        '1. Login and click on the "Funding" tab.',
        "2. Review the shortfall (in pink above) to see the funds needed to continue claim reimbursements.",
        "3. Click on “View” under the “Online Bank” option for instructions on how you can add National HealthClaim as a payee and your account number.",
      ],
    },
    {
      title: "What Claims Got Reimbursed?",
      slug: "227128247-What-Claims-Got-Reimbursed-",
      steps: [
        '1. From the Home page of your Plan Administrator Dashboard, click on "Reports".',
        '2. On the left side of the screen, click on "Funding Activity and Transactions".',
        {
          text: "You can use the Date Selector to generate reports for custom timeframe.",
          substeps: [
            '1. Click the calendar icon next to "From:" at the top of the report.',
            "2. Use the < and > to navigate between months.",
            "3. Use the « and » to navigate between years.",
            '4. Repeat these steps for the calendar icon next to "To:".',
            "5. The report will automatically update to show data for your selected date range.",
          ],
        },
      ],
    },
    {
      title: "How fast do claims get reimbursed?",
      slug: "227129327-How-fast-do-claims-get-reimbursed-",
      imageUrl:
        "https://support.nationalhealthclaim.com/hc/article_attachments/12224277234459",
      steps: [
        "This is one of most common questions our support team gets from employees, and it often directly tied to the funding on the account. Employees who have great claiming experiences are hugely important to the success of this benefit and your role.",
        "National HealthClaim wants  to work together with you to have as many great claim experiences as we can, so we created a behind-the-scenes graphic on how funding and claiming work together.",
        "These timeframes are based on average calculations, and may vary depending on other factors.",
        "It is always our recommendation for companies, when possible, to use pre-funding (funding prior to claims being processed) and online bank payment services or pre-authorized debit for the fastest turn-around for employees.",
        "To see if your account is eligible for pre-authorized debit (PAD), please email our support team at support@nationalhealthclaim.com. ",
      ],
    },
    {
      title: "Adjusting Benefit Limits",
      slug: "227119727-Editing-Benefit-Categories",
      videoPlayer:
        "https://player.vimeo.com/video/876964687?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479",
      steps: [
        '1.Click on the "Employees" tab.',
        '2.Click on or search for the employee this change will apply to.',
        '3. Then click on the employee\'s "Benefit Limits" tab.',
        '4. Scroll down to the "Category" section and click on the blue "Edit" button.',
        '5. Click on the dropdown box and choose the new benefit category the employee is being moved into.',
        '6. Then click save.',
        'Note: This change will take effect immediately for the employee, and the increase will take into account the previous usage of the employee.',
      ],
    },
  ];

  const sectionCardStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: "0.75rem",
    padding: "0.75rem",
    border: "1px solid rgba(0, 0, 0, 0.06)",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
  };

  return (
    <div
      className="position-fixed bottom-0 end-0"
      style={{ zIndex: 1050, fontSize: "13px", padding: "1rem" }}
    >
      <div style={{ position: "relative" }}>
        {isOpen && (
          <div
            style={{
              width: "23rem",
              position: "absolute",
              bottom: "100%",
              right: 0,
              marginBottom: "0.875rem",
              borderRadius: "1.25rem",
              overflow: "hidden",
              background: "#f5f7fb",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.08)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "min(calc(100vh - 5rem), 38rem)",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)",
                color: "#ffffff",
                padding: "1rem 1.125rem",
                position: "relative",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
                style={{
                  position: "absolute",
                  top: "0.75rem",
                  right: "0.75rem",
                  background: "transparent",
                  border: "none",
                  color: "#ffffff",
                  opacity: 0.85,
                  cursor: "pointer",
                  padding: "0.25rem",
                  borderRadius: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={18} />
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
                <div
                  style={{
                    width: "2rem",
                    height: "2rem",
                    borderRadius: "0.5rem",
                    background: "rgba(255, 255, 255, 0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <HelpCircle size={18} />
                </div>
                <div>
                  <div style={{ fontSize: "1rem", fontWeight: 600, lineHeight: 1.2 }}>
                    Need Help?
                  </div>
                  <div style={{ fontSize: "0.75rem", opacity: 0.9, marginTop: "0.125rem" }}>
                    We're here to help you get things done.
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                padding: "0.75rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.625rem",
                overflowY: "auto",
                flex: "1 1 auto",
                minHeight: 0,
              }}
            >
              <div style={sectionCardStyle}>
                <Button
                  variant="outline-primary"
                  className="w-100 d-flex align-items-center justify-content-center"
                  style={{
                    gap: "0.5rem",
                    borderRadius: "0.625rem",
                    padding: "0.625rem 0.75rem",
                    fontWeight: 500,
                  }}
                  onClick={() =>
                    window.open(
                      "https://calendly.com/training-nationalhealthclaim/30min",
                      "_blank"
                    )
                  }
                >
                  <CalendarDays size={16} />
                  Book a Training Session
                </Button>
              </div>

              <div style={sectionCardStyle}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontWeight: 600,
                    marginBottom: "0.625rem",
                  }}
                >
                  <Video size={16} color="#0d6efd" />
                  <span>View a video or support article</span>
                </div>
                <div
                  style={{
                    padding: "56.25% 0 0 0",
                    position: "relative",
                    marginBottom: "0.875rem",
                    borderRadius: "0.625rem",
                    overflow: "hidden",
                    background: "#000",
                  }}
                >
                  <iframe
                    src="https://vimeo.com/showcase/10828343/embed"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: 0,
                    }}
                    allowFullScreen
                  ></iframe>
                </div>
                <div style={{ fontWeight: 600, marginBottom: "0.375rem" }}>
                  Show me how to:
                </div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {SUPPORT_ARTICLES.map((article) => (
                    <li key={article.slug}>
                      <a
                        className="support-link"
                        href={`${supportUrl}/${article.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#0d6efd",
                          textDecoration: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "0.5rem",
                          padding: "0.375rem 0.5rem",
                          borderRadius: "0.5rem",
                          transition: "background 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(13, 110, 253, 0.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span>{article.title}</span>
                        <ExternalLink size={14} style={{ flexShrink: 0, opacity: 0.75 }} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div style={sectionCardStyle}>
                <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Help</div>
                <div style={{ color: "#495057", lineHeight: 1.5 }}>
                  Contact Support at{" "}
                  <a
                    className="RT-Link"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      color: "#0d6efd",
                    }}
                    href="mailto:support@nationalhealthclaim.com"
                  >
                    <Mail size={13} />
                    support@nationalhealthclaim.com
                  </a>{" "}
                  or call our toll-free number{" "}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", whiteSpace: "nowrap" }}>
                    <Phone size={13} style={{ color: "#6c757d" }} />
                    1-866-342-5908
                  </span>{" "}
                  during regular business hours. Monday - Friday 8am - 4pm MST
                </div>
              </div>
            </div>
          </div>
        )}
        <Button
          variant="primary"
          className="rounded-circle d-flex align-items-center justify-content-center shadow"
          style={{
            width: "3rem",
            height: "3rem",
            padding: 0,
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
          }}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Help"
        >
          <HelpCircle size={22} />
        </Button>
      </div>
    </div>
  );
};

export default FloatingHelpButton;
